import React, { useState } from "react";
import DOMPurify from "dompurify";

// ─── Main entry point ─────────────────────────────────────────────────────────
const FormatResponse = ({ text, message, onFollowUpClick, isLastMessage }) => {
  if (message && (message.answerText || message.reasoning || message.followUpQuestions)) {
    return (
      <StructuredResponse
        answerText={message.answerText || message.response}
        reasoning={message.reasoning}
        followUpQuestions={message.followUpQuestions}
        onFollowUpClick={onFollowUpClick}
        isLastMessage={isLastMessage}
      />
    );
  }
  return <MarkdownRenderer text={text || ""} />;
};

// ─── Structured response ──────────────────────────────────────────────────────
const StructuredResponse = ({ answerText, reasoning, followUpQuestions, onFollowUpClick, isLastMessage }) => {
  const [reasoningOpen, setReasoningOpen] = useState(false);

  let questions = [];
  if (Array.isArray(followUpQuestions)) {
    questions = followUpQuestions.filter(Boolean);
  } else if (typeof followUpQuestions === "string") {
    questions = followUpQuestions
      .split(/\n|\d+\.\s/)
      .map((q) => q.trim())
      .filter((q) => q.length > 8);
  }

  return (
    <div className="structured-response">
      <div className="sr-answer">
        <MarkdownRenderer text={answerText || ""} />
      </div>

      {reasoning && (
        <div className="sr-reasoning-block">
          <button className="sr-toggle-btn" onClick={() => setReasoningOpen(!reasoningOpen)}>
            <span className="sr-toggle-icon">{reasoningOpen ? "▾" : "▸"}</span>
            Reasoning &amp; Sources
          </button>
          {reasoningOpen && (
            <div className="sr-reasoning-body">
              <MarkdownRenderer text={reasoning} />
            </div>
          )}
        </div>
      )}

      {isLastMessage && questions.length > 0 && (
        <div className="sr-followups">
          <p className="sr-followups-label">Suggested follow-ups</p>
          <div className="sr-chips">
            {questions.map((q, i) => (
              <button key={i} className="sr-chip"
                onClick={() => onFollowUpClick && onFollowUpClick(q)}>
                {q.length > 80 ? q.slice(0, 80) + "…" : q}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Disclaimer icon ──────────────────────────────────────────────────────────
export const DisclaimerIcon = ({ disclaimer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="disclaimer-icon-wrapper">
      <button className="disclaimer-icon-btn" onClick={() => setOpen(!open)}
        title="View clinical disclaimer" aria-label="Show clinical disclaimer">
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="9" stroke="#e6a817" strokeWidth="1.8"/>
          <path d="M10 9v5" stroke="#e6a817" strokeWidth="1.8" strokeLinecap="round"/>
          <circle cx="10" cy="6.5" r="0.9" fill="#e6a817"/>
        </svg>
      </button>
      {open && <div className="disclaimer-popup">{disclaimer}</div>}
    </div>
  );
};

// ─── Inline markdown (bold, italic, code) ────────────────────────────────────
// Applied to individual lines/items so it doesn't fight block-level parsing
function applyInline(s) {
  // bold+italic
  s = s.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  // bold
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // italic (not at start of line to avoid bullet conflicts)
  s = s.replace(/(?<=\S)\*(?=\S)(.+?)(?<=\S)\*(?=\s|$|[^\w])/g, '<em>$1</em>');
  // inline code
  s = s.replace(/`([^`]+)`/g, '<code class="md-code">$1</code>');
  return s;
}

// ─── Core markdown → HTML parser ─────────────────────────────────────────────
function markdownToHTML(raw) {
  if (!raw) return "";

  // Normalize line endings
  let text = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Step 1: extract fenced code blocks to protect them
  const codeBlocks = [];
  text = text.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => {
    const i = codeBlocks.length;
    codeBlocks.push(`<pre class="md-pre"><code>${esc(code.trim())}</code></pre>`);
    return `\nCODE_BLOCK_${i}\n`;
  });

  const lines = text.split('\n');
  const out = [];
  let inOL = false, inUL = false;

  const closeAll = () => {
    if (inOL) { out.push('</ol>'); inOL = false; }
    if (inUL) { out.push('</ul>'); inUL = false; }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    const trimmed = line.trim();

    // blank line
    if (!trimmed) {
      closeAll();
      continue;
    }

    // code block placeholder
    const cbMatch = trimmed.match(/^CODE_BLOCK_(\d+)$/);
    if (cbMatch) {
      closeAll();
      out.push(codeBlocks[parseInt(cbMatch[1])]);
      continue;
    }

    // headings
    const hMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
    if (hMatch) {
      closeAll();
      const lvl = hMatch[1].length;
      out.push(`<h${lvl} class="md-h${lvl}">${applyInline(hMatch[2])}</h${lvl}>`);
      continue;
    }

    // ordered list: lines starting with "1." "2." "1)" etc.
    const olMatch = trimmed.match(/^(\d+)[.)]\s+([\s\S]+)/);
    if (olMatch) {
      if (inUL) { out.push('</ul>'); inUL = false; }
      if (!inOL) { out.push('<ol class="md-ol">'); inOL = true; }
      // Peek at next lines for continuation (indented content)
      let content = olMatch[2];
      while (i + 1 < lines.length) {
        const next = lines[i + 1];
        // continuation line: indented or starts with a colon after bold
        if (next.match(/^\s{2,}/) || next.trim().match(/^[:(]/)) {
          content += ' ' + next.trim();
          i++;
        } else break;
      }
      out.push(`<li>${applyInline(content)}</li>`);
      continue;
    }

    // unordered list
    const ulMatch = trimmed.match(/^[-*•]\s+([\s\S]+)/);
    if (ulMatch) {
      if (inOL) { out.push('</ol>'); inOL = false; }
      if (!inUL) { out.push('<ul class="md-ul">'); inUL = true; }
      out.push(`<li>${applyInline(ulMatch[1])}</li>`);
      continue;
    }

    // normal paragraph
    closeAll();
    out.push(`<p class="md-p">${applyInline(trimmed)}</p>`);
  }

  closeAll();
  return out.join('\n');
}

function esc(s) {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}

// ─── MarkdownRenderer ─────────────────────────────────────────────────────────
const MarkdownRenderer = ({ text }) => {
  const html = DOMPurify.sanitize(markdownToHTML(text || ""), {
    ALLOWED_TAGS: ['p','strong','em','h1','h2','h3','ul','ol','li','code','pre','br','span','div'],
    ALLOWED_ATTR: ['class']
  });
  return <div className="md-body" dangerouslySetInnerHTML={{ __html: html }} />;
};

export default FormatResponse;